import { Upload } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface DropOverlayProps {
  children: ReactNode;
  onFilesDropped: (files: File[]) => void;
  message?: string;
}

export function DropOverlay({ children, onFilesDropped, message = "Drop files here" }: Readonly<DropOverlayProps>) {
  const dragCounter = useRef(0);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  useEffect(() => {
    const handleDragEnter = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes("Files")) return;
      dragCounter.current += 1;
      setIsDraggingOver(true);
    };
    const handleDragLeave = () => {
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setIsDraggingOver(false);
    };
    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
    };
    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      dragCounter.current = 0;
      setIsDraggingOver(false);
      const dropped = event.dataTransfer?.files;
      if (dropped && dropped.length > 0) onFilesDropped(Array.from(dropped));
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);
    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, [onFilesDropped]);

  return (
    <>
      {children}
      {isDraggingOver &&
        createPortal(
          <div
            aria-hidden="true"
            className="pointer-events-none fixed inset-0 z-[100] bg-surface-base/40 backdrop-blur-md"
          >
            <div className="absolute inset-3 flex items-center justify-center rounded-2xl border-2 border-brand-primary">
              <div className="flex flex-col items-center gap-3">
                <Upload size={28} className="text-brand-primary" />
                <p className="text-[var(--type-body)] font-medium text-content-primary">{message}</p>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
